import React, { useEffect, useState } from "react";
import { X, ArrowSquareOut, Megaphone, User as UserIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const YT_RE = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/;
function ytEmbedUrl(url) {
  const m = url?.match(YT_RE);
  return m ? `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0&modestbranding=1` : null;
}

/**
 * MuralPopup — popup compacto que aparece após login (cliente ou membro).
 * Se o mural existir, mostra 1 popup empilhando os murais disponíveis.
 * X dispensa por sessão (sessionStorage). Próximo login mostra de novo.
 * Só some pra sempre quando o dono ou admin apagar.
 *
 * Props:
 *   audience: "client" | "member"
 */
export default function MuralPopup({ audience = "client" }) {
  const [murals, setMurals] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const endpoint = audience === "member" ? "/mural/for-member" : "/mural/for-me";
    api.get(endpoint)
      .then(({ data }) => {
        const list = (data.murals || []).filter((m) => m && (m.title || m.body || m.links?.length || m.images?.length || m.videos?.length));
        setMurals(list);
        // Verifica sessionStorage — pra não repetir se user fechou nessa sessão
        const dismissedIds = JSON.parse(sessionStorage.getItem("_mural_dismissed") || "[]");
        const remaining = list.filter((m) => !dismissedIds.includes(`${m.id}_${m.updated_at}`));
        if (remaining.length === 0) setDismissed(true);
        else {
          const firstRemainingIdx = list.findIndex((m) => !dismissedIds.includes(`${m.id}_${m.updated_at}`));
          setIdx(Math.max(0, firstRemainingIdx));
        }
      })
      .catch(() => setDismissed(true));
  }, [audience]);

  const m = murals[idx];

  const close = () => {
    if (!m) { setDismissed(true); return; }
    const dismissedIds = JSON.parse(sessionStorage.getItem("_mural_dismissed") || "[]");
    dismissedIds.push(`${m.id}_${m.updated_at}`);
    sessionStorage.setItem("_mural_dismissed", JSON.stringify(dismissedIds));
    const next = murals.findIndex((x, i) => i > idx && !dismissedIds.includes(`${x.id}_${x.updated_at}`));
    if (next === -1) setDismissed(true);
    else setIdx(next);
  };

  // Fecha com ESC (hooks devem vir antes de early return)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, murals]);

  if (dismissed || murals.length === 0 || !m) return null;
  const isGlobal = m.scope === "global";

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 fadeUp"
      role="dialog"
      data-testid="mural-popup"
      onClick={(e) => {
        // Fecha ao clicar no backdrop (fora do card)
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto"
        style={{
          borderColor: isGlobal ? "var(--accent)" : "var(--border)",
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between gap-2 px-4 py-2.5 border-b border-white/5"
          style={{
            background: isGlobal ? "var(--accent)" : "var(--bg-surface)",
            color: isGlobal ? "#000" : "inherit",
          }}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {isGlobal ? (
              <Megaphone size={13} weight="fill" />
            ) : (
              <UserIcon size={13} weight="bold" />
            )}
            <span className="text-[10px] uppercase tracking-widest font-bold">
              {isGlobal ? "Aviso da plataforma" : "Mural do produtor"}
            </span>
          </div>
          <button
            onClick={close}
            data-testid="mural-close"
            className="p-1 rounded-sm hover:bg-black/10 transition"
            title="Fechar"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {m.title && (
            <div className="font-display font-black text-lg leading-tight" data-testid="mural-title">
              {m.title}
            </div>
          )}
          {m.body && (
            <div className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed" data-testid="mural-body">
              {m.body}
            </div>
          )}

          {/* Imagens */}
          {m.images?.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {m.images.map((img, i) => (
                <img
                  key={`img-${i}`}
                  src={img.url}
                  alt={img.alt || ""}
                  className="w-full h-auto rounded-sm border border-white/10 object-cover max-h-40"
                  data-testid={`mural-img-${i}`}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              ))}
            </div>
          )}

          {/* Vídeos (pequenos abaixo do mural) */}
          {m.videos?.length > 0 && (
            <div className="space-y-2">
              {m.videos.map((v, i) => {
                const embed = ytEmbedUrl(v.url);
                return (
                  <div key={`vid-${i}`} className="rounded-sm overflow-hidden border border-white/10 bg-black">
                    {v.name && <div className="px-2 py-1 text-[10px] text-white/60 bg-white/5">{v.name}</div>}
                    {embed ? (
                      <iframe
                        src={embed}
                        title={v.name || "video"}
                        className="w-full"
                        style={{ aspectRatio: "16/9" }}
                        allow="encrypted-media; picture-in-picture"
                        allowFullScreen
                        data-testid={`mural-video-${i}`}
                      />
                    ) : (
                      <video
                        src={v.url}
                        controls
                        controlsList="nodownload"
                        className="w-full"
                        style={{ aspectRatio: "16/9" }}
                        data-testid={`mural-video-${i}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Links / botões */}
          {m.links?.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {m.links.map((l, i) => (
                <a
                  key={`ln-${i}`}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`mural-link-${i}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[var(--accent)] text-black text-xs font-bold hover:brightness-110 transition"
                >
                  <ArrowSquareOut size={11} weight="bold" />
                  {l.label || "Abrir"}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Footer nav se tiver mais de 1 mural */}
        {murals.length > 1 && (
          <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between text-[10px] text-white/50">
            <span data-testid="mural-count">
              {idx + 1} de {murals.length}
            </span>
            <button
              onClick={close}
              data-testid="mural-next"
              className="text-[var(--accent)] hover:underline"
            >
              Próximo aviso →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
